import classNames from 'classnames';
import {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    useMemo,
} from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_BOOKING_STAGE_TYPES, QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import styles from './quiz-select-field.module.scss';
import { ItemProps } from '../QuizMultiSelectField/QuizMultiSelectField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useWaitTime } from 'src/hooks/useWaitTime';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle as faInfoCircleSolid } from '@fortawesome/pro-solid-svg-icons';
import MoshModal from 'src/components/MoshModal';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { Vertical } from 'src/constants/Verticals';
import { env, isMoshy } from 'src/env.mjs';

const QUIZ_SELECT_SUBMIT_DELAY = 300;
export const QUIZ_SELECT_SMALL_ANSWER_LIMIT = 5;

const QUIZ_BOOK_NEXT_DOCTOR_ESTIMATE_WAIT_TEXT = 'Estimated wait time';
const QUIZ_BOOK_NEXT_DOCTOR_NONE_WAIT_TEXT =
    'No practitioners currently available';

type QuizSelectFieldProps = {
    showWaitTime?: boolean;
};

const QuizSelectField = ({ showWaitTime = false }: QuizSelectFieldProps) => {
    const {
        quizName,
        quizVersion,
        quizType,
        quizToken = '',
        currentQuestion,
        handleSubmitAnswer,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        state,
    } = useContext(QuizContext);
    const { data } = useWaitTime({ quizToken }, showWaitTime);
    const { waitStage, waitTime } = data || {};
    const currentBookingStage = useMemo(
        () => (waitStage ? QUIZ_BOOKING_STAGE_TYPES[waitStage] : null),
        [waitStage],
    );
    const {
        answerList,
        questionId,
        questionText,
        questionExplanation,
        additionalField1: modalHeading,
        additionalField2: modalDescription,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        medication,
    } = currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [, setContinueDisabled] = state.continueDisabled;
    const [, setContinueVisible] = state.continueVisible;
    const [loading] = state.loading;
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ItemProps | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Handle submitting
    const handleSubmit = useCallback(
        (i: number) => {
            const selectedAnswer = i !== null ? answerList[i] : null;
            if (selectedAnswer && questionId) {
                handleSubmitAnswer(
                    {
                        questionId: questionId,
                        questionText: questionText,
                        answerIds: [selectedAnswer.answerId],
                        answerTexts: [selectedAnswer.answerText],
                        medication: medication,
                    },
                    () => {
                        // Redirect to answer's URL if exists
                        const { redirectUrl } = selectedAnswer;
                        if (redirectUrl) window.location.href = redirectUrl;
                    },
                );
            }
        },
        [answerList, questionId, questionText, medication, handleSubmitAnswer],
    );
    const handleSelectAnswer = useCallback(
        (i: number) => {
            // Set selected answer
            setSelectedIndex(selectedIndex === i ? null : i);
            // Clear existing submit timeout if exists
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            // Set new submit timeout
            if (selectedIndex !== i) {
                timeoutRef.current = setTimeout(() => {
                    handleSubmit(i);
                }, QUIZ_SELECT_SUBMIT_DELAY);
            }
        },
        [selectedIndex, handleSubmit],
    );

    const handleOpenModal = (
        item: ItemProps,
        e?: React.MouseEvent<HTMLElement>,
    ) => {
        if (e) e.stopPropagation();
        setSelectedItem(item);
        setShowModal(true);
        trackClick(EventNames.QUIZ_BUTTON_CLICKED, {
            quiz_name: `${quizName} - ${quizVersion}`,
            vertical: Vertical[quizType.toUpperCase()],
            label: 'Why are we asking for this',
            question_name: questionText,
        });
    };

    // Set existing answer
    useEffect(() => {
        let selectedAnswerId = '';
        if (
            questionId &&
            submittedQuestionIds.includes(questionId) &&
            userAnswers.length > 0
        ) {
            selectedAnswerId = userAnswers[0].answerId;
        } else if (
            inQueue &&
            prefillNeeded &&
            prefillQuestionIds.includes(questionId) &&
            prefillAnswers.length > 0
        ) {
            selectedAnswerId = prefillAnswers[0].answerId;
        }
        if (selectedAnswerId) {
            setSelectedIndex(
                answerList.findIndex((a) => a.answerId === selectedAnswerId),
            );
        } else {
            setSelectedIndex(null);
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        answerList,
    ]);
    // Continue click listener
    useEffect(() => {
        const submitCurrentSelectedAnswer = () => {
            if (selectedIndex !== null) handleSubmit(selectedIndex);
        };
        window.addEventListener(
            QUIZ_EVENT_TYPES.continueClick,
            submitCurrentSelectedAnswer,
        );

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                submitCurrentSelectedAnswer,
            );
    }, [handleSubmit, selectedIndex]);
    // Enter / Number key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && selectedIndex !== null)
                handleSubmit(selectedIndex);
            if (parseInt(e.key)) {
                const num = parseInt(e.key);
                if (num <= answerList.length) handleSelectAnswer(num - 1);
            }
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [answerList, handleSubmit, handleSelectAnswer, selectedIndex]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(false);
            if (
                (userAnswers.length > 0 && selectedIndex !== null) ||
                (inQueue &&
                    prefillNeeded &&
                    prefillQuestionIds.includes(questionId) &&
                    prefillAnswers.length > 0)
            ) {
                setContinueVisible(true);
            } else {
                setContinueVisible(false);
            }
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        pageTransitioning,
        inQueue,
        selectedIndex,
        setContinueDisabled,
        setContinueVisible,
    ]);

    return (
        <div
            className={classNames(styles['quiz-select-option--wrapper'], {
                [styles['loading']]: loading,
            })}
        >
            {/* Content header */}
            <div className={classNames(styles['quiz-field-header'])}>
                {/* Title */}
                <h4 className={classNames(styles['title'])}>{questionText}</h4>
                {/* Subtitle */}
                {questionExplanation ? (
                    <h5
                        className={classNames(
                            styles['subtitle'],
                            'highlight pt-2',
                        )}
                    >
                        {questionExplanation}
                    </h5>
                ) : null}
                {showWaitTime ? (
                    <div
                        className={classNames(
                            styles['wait-time--wrapper'],
                            'highlighT flex w-fit items-center rounded-3xl px-3 py-2',
                        )}
                    >
                        <FontAwesomeIcon
                            icon={faClock}
                            className="light-icon mr-2"
                        />
                        {currentBookingStage !== null
                            ? {
                                  [QUIZ_BOOKING_STAGE_TYPES.short]: (
                                      <h5>
                                          {`${QUIZ_BOOK_NEXT_DOCTOR_ESTIMATE_WAIT_TEXT} ${waitTime}`}
                                      </h5>
                                  ),
                                  [QUIZ_BOOKING_STAGE_TYPES.long]: (
                                      <h5>
                                          {`${QUIZ_BOOK_NEXT_DOCTOR_ESTIMATE_WAIT_TEXT} ${waitTime}`}
                                      </h5>
                                  ),
                                  [QUIZ_BOOKING_STAGE_TYPES.several_days]: (
                                      <h5>
                                          {`${QUIZ_BOOK_NEXT_DOCTOR_ESTIMATE_WAIT_TEXT} ${waitTime}`}
                                      </h5>
                                  ),
                                  [QUIZ_BOOKING_STAGE_TYPES.none]: (
                                      <h5>
                                          {`${QUIZ_BOOK_NEXT_DOCTOR_NONE_WAIT_TEXT}`}
                                      </h5>
                                  ),
                              }[currentBookingStage]
                            : null}
                    </div>
                ) : null}
                {modalHeading ? (
                    <button
                        className={classNames(
                            styles['quiz-info--wrapper'],
                            'mt-2 flex items-center gap-x-2',
                            !(modalHeading && modalDescription)
                                ? 'cursor-default'
                                : '',
                        )}
                        onClick={() => {
                            if (modalHeading && modalDescription) {
                                handleOpenModal({
                                    answerHeading: modalHeading,
                                    answerDescription: modalDescription,
                                });
                            }
                        }}
                    >
                        {modalHeading && modalDescription ? (
                            <span className="h-6 w-6">
                                <FontAwesomeIcon
                                    icon={
                                        isMoshy
                                            ? faInfoCircle
                                            : faInfoCircleSolid
                                    }
                                    className="mr-2 !h-full w-full"
                                />
                            </span>
                        ) : null}
                        <h5 className={styles['quiz-info--message']}>
                            {modalHeading}
                        </h5>
                    </button>
                ) : null}
            </div>
            {/* List of answers */}
            <div
                className={classNames(
                    styles['answers-list'],
                    answerList.length > QUIZ_SELECT_SMALL_ANSWER_LIMIT
                        ? styles['item--wrap']
                        : '',
                )}
            >
                {/* Answer */}
                {answerList.map((a, i) => (
                    <button
                        key={a.answerId}
                        data-testid="quiz-answer"
                        className={classNames(
                            styles['answer-item'],
                            i === selectedIndex ? styles['item-selected'] : '',
                            loading
                                ? 'pointer-events-none'
                                : 'pointer-events-auto',
                        )}
                        onClick={() => handleSelectAnswer(i)}
                        disabled={loading}
                    >
                        <span
                            className={classNames(
                                styles['item-number'],
                                'p1 highlight',
                            )}
                        >
                            {i + 1}
                        </span>
                        <span className={classNames(styles['item-text'], 'p1')}>
                            {a.answerText}
                        </span>
                        {/* Show info popup if additional info provided */}
                        {a.additionalField1 ? (
                            <button
                                className={classNames(styles['info-button'])}
                                onClick={(e) =>
                                    handleOpenModal(
                                        {
                                            answerHeading: a.answerText,
                                            answerDescription:
                                                a.additionalField1,
                                        },
                                        e,
                                    )
                                }
                            >
                                <FontAwesomeIcon
                                    icon={
                                        isMoshy
                                            ? faInfoCircle
                                            : faInfoCircleSolid
                                    }
                                    className="!h-auto w-6"
                                />
                            </button>
                        ) : null}
                        {a.image ? (
                            <img
                                src={a.image}
                                alt={a.answerText}
                                className="h-16 w-16"
                            />
                        ) : null}
                    </button>
                ))}
                {showModal && selectedItem && (
                    <MoshModal
                        open={showModal}
                        ariaLabel={selectedItem.answerHeading}
                        onModalClose={(value) => setShowModal(value || false)}
                        custom={true}
                        theme={`theme-${env.NEXT_PUBLIC_BRAND}`}
                    >
                        <div>
                            <h4 className="first-letter:capitalize md:pr-2">
                                {selectedItem.answerHeading}
                            </h4>
                            <p className="mb-0 mt-2">
                                {selectedItem.answerDescription}
                            </p>
                        </div>
                    </MoshModal>
                )}
            </div>
        </div>
    );
};

export default QuizSelectField;
